/**
 * useLightsCaptureSession — 조명 dry-run 캡처 세션을 캡슐화한 훅.
 *
 * LightsSimulator 와 EmergencyBellSimulator 가 공유하는 부분만 추출했다:
 *   dry-run 토글(setMode) + getEvents 폴링 + cursor + 이벤트 누적 +
 *   statusStore 와이어링(updateLightingStatus/setLightingBaseline/clearLighting) +
 *   unmount 정리.
 *
 * 트리거(모션/비상/직접제어)나 도달 체크 같은 화면별 동작은 포함하지 않는다 —
 * 호출 측에서 LightsSimulatorService 를 직접 부른다.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  LightsSimulatorService,
  type SimEvent,
} from '../../../services/LightsSimulatorService';
import { useStatusStore } from '../../../map-editor-features/status/model/statusStore';

const POLL_INTERVAL_MS = 1000;

/**
 * light type 코드 → 한국어 레이블.
 * events 의 lightType 은 dimmer 노드에서 해석된 값(aisle/parking/caution/warning,
 * 조합 시 콤마)이다. control 트리거 어휘(dimming/warnLamp)도 별칭으로 받아준다.
 */
export function lightTypeLabel(t: string): string {
  if (t === 'aisle' || t === 'dimming') return '통로등';
  if (t === 'parking') return '만공차등';
  if (t === 'caution') return '충돌방지등';
  if (t === 'warning' || t === 'warnLamp') return '경광등';
  return t || '조명';
}

/** SimEvent → LightingStatus 변환 + 스토어 업데이트 */
export function applyEventToStore(
  ev: SimEvent,
  updateLightingStatus: ReturnType<typeof useStatusStore.getState>['updateLightingStatus'],
): void {
  updateLightingStatus({
    cctvName: ev.cctvName,
    section: ev.section,
    lightType: lightTypeLabel(ev.lightType),
    on: ev.on,
    dimming: ev.value,
    lastUpdate: Date.now(),
  });
}

/** 이벤트 패널 행 */
export interface EventRow {
  id: number;
  at: string;
  cctvName: string;
  lightType: string;
  mode: string;
  value: number;
  on: boolean;
}

export interface LightsCaptureSession {
  /** dry-run 캡처 모드 활성 여부 */
  dryRunEnabled: boolean;
  /** 토글 진행 중 여부 */
  togglingMode: boolean;
  /** 토글 실패 메시지 */
  modeError: string | null;
  /** dry-run 토글 (setMode) */
  toggleMode: () => Promise<void>;
  /** 캡처된 fan-out 이벤트 (최신순, 최대 200) */
  events: EventRow[];
  /** 이벤트 패널 + 조명 하이라이트 초기화 */
  clearEvents: () => void;
}

/**
 * dry-run 캡처 세션 훅. 마운트 컴포넌트 1개당 1개씩 사용한다.
 * (LightsSimulator·EmergencyBellSimulator 가 동시에 마운트되지 않으므로 충돌 없음.)
 */
export function useLightsCaptureSession(): LightsCaptureSession {
  const [dryRunEnabled, setDryRunEnabled] = useState(false);
  const [togglingMode, setTogglingMode] = useState(false);
  const [modeError, setModeError] = useState<string | null>(null);
  const [events, setEvents] = useState<EventRow[]>([]);

  const cursorRef = useRef<number>(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const updateLightingStatus = useStatusStore(s => s.updateLightingStatus);
  const clearLighting = useStatusStore(s => s.clearLighting);
  const setLightingBaseline = useStatusStore(s => s.setLightingBaseline);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollEvents = useCallback(async () => {
    try {
      const resp = await LightsSimulatorService.getEvents(cursorRef.current);
      if (resp.cursor > cursorRef.current) {
        cursorRef.current = resp.cursor;
      }
      if (resp.events.length > 0) {
        const newRows: EventRow[] = resp.events.map(ev => ({
          id: ev.seq,
          at: new Date(ev.ts).toLocaleTimeString(),
          cctvName: ev.cctvName,
          lightType: lightTypeLabel(ev.lightType),
          mode: ev.mode,
          value: ev.value,
          on: ev.on,
        }));
        setEvents(prev => [...newRows, ...prev].slice(0, 200));
        resp.events.forEach(ev => applyEventToStore(ev, updateLightingStatus));
      }
      if (!resp.enabled && dryRunEnabled) {
        // 서버 측에서 dry-run 이 꺼진 경우 — 폴링·유휴 베이스라인도 함께 정리
        setDryRunEnabled(false);
        stopPolling();
        setLightingBaseline(false);
      }
    } catch {
      // 폴 실패는 화면을 막지 않음
    }
  }, [dryRunEnabled, stopPolling, updateLightingStatus, setLightingBaseline]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(pollEvents, POLL_INTERVAL_MS);
  }, [stopPolling, pollEvents]);

  // unmount 시 폴링 + 유휴 베이스라인 정리 (베이스라인이 에디터/뷰어로 새지 않도록)
  useEffect(() => {
    return () => {
      stopPolling();
      setLightingBaseline(false);
      clearLighting();
    };
  }, [stopPolling, setLightingBaseline, clearLighting]);

  const toggleMode = useCallback(async () => {
    setModeError(null);
    setTogglingMode(true);
    try {
      const next = !dryRunEnabled;
      const resp = await LightsSimulatorService.setMode(next);
      setDryRunEnabled(resp.enabled);
      if (resp.enabled) {
        cursorRef.current = 0;
        startPolling();
        // 유휴 베이스라인 ON — 모든 통로등 CCTV 를 20%로 은은하게 표시
        setLightingBaseline(true);
      } else {
        stopPolling();
        clearLighting();
        setLightingBaseline(false);
      }
    } catch (e) {
      setModeError(e instanceof Error ? e.message : 'dry-run 모드 전환 실패');
    } finally {
      setTogglingMode(false);
    }
  }, [dryRunEnabled, startPolling, stopPolling, clearLighting, setLightingBaseline]);

  const clearEvents = useCallback(() => {
    setEvents([]);
    clearLighting();
  }, [clearLighting]);

  return {
    dryRunEnabled,
    togglingMode,
    modeError,
    toggleMode,
    events,
    clearEvents,
  };
}
